import { Trash2 } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type {
  Connection,
  ConnectionMapping,
  Pipeline,
  Tenant,
} from "../../../../api/client.ts";
import CrmProviderLogo from "../../../../components/CrmProviderLogo.tsx";
import EmptyTableRow from "../../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../../components/table/ScrollTable.tsx";

interface TenantMappingsDialogProps {
  connection: Connection | null;
  // Shown above the mappings, e.g. when a new connection still needs one.
  notice?: string | null;
  mappings: ConnectionMapping[] | undefined;
  mappingsLoading: boolean;
  pipelines: Pipeline[] | undefined;
  tenants: Tenant[] | undefined;
  orgTenantIds: Set<string>;
  tenantName: (tid: string) => string;
  mappingTenantId: string;
  onMappingTenantChange: (value: string) => void;
  mappingPipelineId: string;
  onMappingPipelineChange: (value: string) => void;
  onAddMapping: () => void;
  onDeleteMapping: (mappingId: string) => void;
  addPending: boolean;
  deletePending: boolean;
  onClose: () => void;
}

export default function TenantMappingsDialog({
  connection,
  notice,
  mappings,
  mappingsLoading,
  pipelines,
  tenants,
  orgTenantIds,
  tenantName,
  mappingTenantId,
  onMappingTenantChange,
  mappingPipelineId,
  onMappingPipelineChange,
  onAddMapping,
  onDeleteMapping,
  addPending,
  deletePending,
  onClose,
}: TenantMappingsDialogProps) {
  const orgScopedMappings = mappings?.filter((m) => orgTenantIds.has(m.tenant_id)) ?? [];
  return (
    <Dialog open={!!connection} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          {connection && <CrmProviderLogo provider={connection.provider} />}
          <span>Tenants — {connection?.name || connection?.provider}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {notice && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {notice}
          </Alert>
        )}
        {mappingsLoading ? (
          <CircularProgress size={24} />
        ) : (
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Pipeline</TableCell>
                <TableCell width={60}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgScopedMappings.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{tenantName(m.tenant_id)}</TableCell>
                  <TableCell>
                    {m.pipeline_id
                      ? (pipelines?.find((p) => p.pipeline_id === m.pipeline_id)?.name ??
                        m.pipeline_id)
                      : "All pipelines"}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => onDeleteMapping(m.id)}
                      disabled={deletePending}
                    >
                      <Trash2 fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {orgScopedMappings.length === 0 && (
                <EmptyTableRow colSpan={3} align="left">
                  No mappings in this organization
                </EmptyTableRow>
              )}
            </TableBody>
          </ScrollTable>
        )}

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Add tenant
        </Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            select
            label="Tenant"
            size="small"
            value={mappingTenantId}
            onChange={(e) => onMappingTenantChange(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Select tenant...</MenuItem>
            {tenants?.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Pipeline"
            size="small"
            value={mappingPipelineId}
            onChange={(e) => onMappingPipelineChange(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All pipelines</MenuItem>
            {pipelines?.map((p) => (
              <MenuItem key={p.pipeline_id} value={p.pipeline_id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            size="small"
            disabled={!mappingTenantId || addPending}
            onClick={onAddMapping}
          >
            Add
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
